import { QA_API_DOMAIN, fetchWithSupabaseAuth } from "@/api/classifier";
import { supabase } from "@/integrations/supabase/client";
import { useQuery, useQueryClient } from "@tanstack/react-query";

export interface NextUnreviewedEncounter {
  encounterId: string;
  classificationResultId?: string;
}

const QUERY_KEY = "next-unreviewed-encounter";

const NEXT_ENCOUNTER_URL = `${QA_API_DOMAIN}/reviews/next-encounter-to-review`;

const fetchNextUnreviewedEncounter = async ({
  excludeIds = [],
  customerIds,
}: {
  excludeIds?: string[];
  // Omitted/undefined = let the backend pick its default providers
  // (getDefaultCustomerIdsForNextEncounter). Do NOT default to UHCW here, or
  // other providers (e.g. RFT) can never be prioritised.
  customerIds?: string[];
}): Promise<NextUnreviewedEncounter | null> => {
  const userResponse = await supabase.auth.getUser();
  const user = userResponse.data.user;
  if (!user) throw new Error("User not authenticated");

  const response = await fetchWithSupabaseAuth(NEXT_ENCOUNTER_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      excludeIds,
      customerIds,
    }),
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error((err as { error?: string }).error ?? response.statusText);
  }

  if (response.status === 204) return null;

  const body = (await response.json()) as {
    encounterId: string | null;
    classificationResultId?: string;
  };
  const { encounterId, classificationResultId } = body;
  return encounterId ? { encounterId, ...(classificationResultId && { classificationResultId }) } : null;
};

export function useNextUnreviewedEncounter(options?: {
  enabled?: boolean;
  excludeIds?: string[];
  customerIds?: string[];
}) {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: [QUERY_KEY, options?.excludeIds, options?.customerIds],
    queryFn: () =>
      fetchNextUnreviewedEncounter({
        excludeIds: options?.excludeIds,
        customerIds: options?.customerIds,
      }),
    enabled: options?.enabled ?? true,
    staleTime: 0, // Always fetch fresh data
  });

  const refetchWithExcludeIds = async (customExcludeIds: string[]) => {
    return await queryClient.fetchQuery({
      queryKey: [QUERY_KEY, customExcludeIds, options?.customerIds],
      queryFn: () =>
        fetchNextUnreviewedEncounter({
          excludeIds: customExcludeIds,
          customerIds: options?.customerIds,
        }),
      staleTime: 0,
    });
  };

  return {
    ...query,
    refetchWithExcludeIds,
  };
}
